import React, { useState, useEffect } from 'react';
import axios from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface Cephe {
    cepheID: number;
    cepheAdi: string;
    aktif: boolean;
}

interface CepheForm {
    cepheAdi: string;
    aktif: boolean;
}

// Maps common compass direction names to a cardinal letter for the rose icon
const getCompassLetter = (name: string): string => {
    const n = name.toLowerCase();
    if (n.includes('kuzey') && n.includes('doğu')) return 'NE';
    if (n.includes('kuzey') && n.includes('batı')) return 'NW';
    if (n.includes('güney') && n.includes('doğu')) return 'SE';
    if (n.includes('güney') && n.includes('batı')) return 'SW';
    if (n.includes('kuzey')) return 'N';
    if (n.includes('güney')) return 'S';
    if (n.includes('doğu')) return 'E';
    if (n.includes('batı')) return 'W';
    return name.charAt(0).toUpperCase();
};

const directionColors: Record<string, { bg: string; color: string }> = {
    N:  { bg: '#dbeafe', color: '#1d4ed8' },
    NE: { bg: '#ede9fe', color: '#6d28d9' },
    NW: { bg: '#e0f2fe', color: '#0369a1' },
    S:  { bg: '#fef3c7', color: '#b45309' },
    SE: { bg: '#fce7f3', color: '#9d174d' },
    SW: { bg: '#ffedd5', color: '#c2410c' },
    E:  { bg: '#dcfce7', color: '#15803d' },
    W:  { bg: '#f3e8ff', color: '#7e22ce' },
};

const getDirectionStyle = (name: string) => {
    const letter = getCompassLetter(name);
    return directionColors[letter] ?? { bg: '#f1f5f9', color: '#475569' };
};

const Cepheler: React.FC = () => {
    const { t } = useTranslation();
    const [cepheler, setCepheler] = useState<Cephe[]>([]);
    const [selectedCephe, setSelectedCephe] = useState<Cephe | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState<CepheForm>({ cepheAdi: '', aktif: true });

    const fetchCepheler = async () => {
        try {
            const response = await axios.get('/api/Cepheler');
            setCepheler(response.data);
        } catch {
            toast.error(t('ayarlar.cephe.yuklenirkenHata'));
        }
    };

    useEffect(() => { fetchCepheler(); }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (selectedCephe) {
                await axios.put(`/api/Cepheler/${selectedCephe.cepheID}`, formData);
                toast.success(t('ayarlar.cephe.guncellendi'));
            } else {
                await axios.post('/api/Cepheler', formData);
                toast.success(t('ayarlar.cephe.eklendi'));
            }
            fetchCepheler();
            handleCloseModal();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (cephe: Cephe) => {
        setSelectedCephe(cephe);
        setFormData({ cepheAdi: cephe.cepheAdi, aktif: cephe.aktif });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedCephe(null);
        setFormData({ cepheAdi: '', aktif: true });
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

                .cp-page {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    padding: 28px 32px;
                    background: #f4f6fb;
                    min-height: 100vh;
                }

                /* ── Header ── */
                .cp-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 28px;
                }
                .cp-header-left { display: flex; flex-direction: column; gap: 3px; }
                .cp-title {
                    font-size: 22px;
                    font-weight: 700;
                    color: #0f172a;
                    letter-spacing: -0.4px;
                    margin: 0;
                }
                .cp-subtitle { font-size: 13px; color: #94a3b8; font-weight: 400; }
                .cp-add-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 20px;
                    background: #cd6906;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 600;
                    font-family: inherit;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.18s, box-shadow 0.18s, transform 0.12s;
                }
                .cp-add-btn:hover {
                    background: #b85d05;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transform: translateY(-1px);
                }
                .cp-add-btn svg { width: 16px; height: 16px; stroke: #fff; stroke-width: 2.5; fill: none; }

                /* ── Card ── */
                .cp-card {
                    background: #fff;
                    border-radius: 14px;
                    box-shadow: 0 1px 4px rgba(15,23,42,0.07), 0 4px 24px rgba(15,23,42,0.05);
                    overflow: hidden;
                }

                /* ── Table ── */
                .cp-table { width: 100%; border-collapse: collapse; }
                .cp-table thead tr { border-bottom: 1.5px solid #f1f5f9; }
                .cp-table thead th {
                    padding: 13px 20px;
                    font-size: 11px;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #94a3b8;
                    text-align: left;
                    white-space: nowrap;
                }
                .cp-table thead th.cp-th-actions { text-align: right; }
                .cp-table tbody tr {
                    border-bottom: 1px solid #f8fafc;
                    transition: background 0.13s;
                }
                .cp-table tbody tr:last-child { border-bottom: none; }
                .cp-table tbody tr.cp-row-hovered { background: #f0f5ff; }
                .cp-table tbody td {
                    padding: 14px 20px;
                    font-size: 14px;
                    color: #1e293b;
                    vertical-align: middle;
                }

                /* Cell variants */
                .cp-cell-id { font-size: 12px; color: #94a3b8; font-weight: 500; width: 56px; }
                .cp-cell-name { display: flex; align-items: center; gap: 12px; }
                .cp-compass-badge {
                    width: 36px;
                    height: 36px;
                    border-radius: 9px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: -0.3px;
                    flex-shrink: 0;
                }
                .cp-name-text { font-weight: 600; color: #0f172a; font-size: 14px; }
                .cp-cell-durum { width: 110px; }
                .cp-cell-actions { width: 90px; text-align: right; }

                /* Compass SVG ring */
                .cp-compass-ring {
                    position: relative;
                    width: 36px;
                    height: 36px;
                    flex-shrink: 0;
                }
                .cp-compass-ring svg { width: 36px; height: 36px; }

                /* Pill */
                .cp-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 11px 4px 8px;
                    border-radius: 999px;
                    font-size: 12px;
                    font-weight: 600;
                }
                .cp-pill-aktif { background: #dcfce7; color: #15803d; }
                .cp-pill-pasif { background: #fee2e2; color: #b91c1c; }
                .cp-pill-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
                .cp-pill-aktif .cp-pill-dot { background: #22c55e; }
                .cp-pill-pasif .cp-pill-dot { background: #ef4444; }

                /* Ghost buttons */
                .cp-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 6px;
                    opacity: 0;
                    transition: opacity 0.15s;
                }
                .cp-row-hovered .cp-actions { opacity: 1; }
                .cp-ghost-btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 34px; height: 34px;
                    border-radius: 8px;
                    border: 1.5px solid transparent;
                    background: transparent;
                    cursor: pointer;
                    transition: background 0.15s, border-color 0.15s;
                    color: #64748b;
                }
                .cp-ghost-edit:hover { background: #fff8f0; border-color: #f5d5a0; color: #cd6906; }
                .cp-ghost-delete:hover { background: #fff1f2; border-color: #fecdd3; color: #e11d48; }
                .cp-ghost-btn svg {
                    width: 15px; height: 15px;
                    stroke: currentColor; stroke-width: 2;
                    fill: none; stroke-linecap: round; stroke-linejoin: round;
                }

                /* Footer */
                .cp-footer {
                    padding: 13px 20px;
                    border-top: 1.5px solid #f1f5f9;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .cp-count { font-size: 13px; color: #94a3b8; font-weight: 500; }
                .cp-count span { color: #475569; font-weight: 600; }

                /* Empty */
                .cp-empty { text-align: center; padding: 56px 20px; color: #94a3b8; }
                .cp-empty-icon { font-size: 36px; margin-bottom: 10px; opacity: 0.5; }
                .cp-empty-text { font-size: 15px; font-weight: 500; }

                /* ── Modal ── */
                .cp-overlay {
                    position: fixed; inset: 0;
                    background: rgba(0,0,0,0.2);
                    backdrop-filter: blur(2px);
                    display: flex; justify-content: flex-end;
                    z-index: 1050;
                }
                .cp-modal {
                    background: #fff;
                    border-radius: 16px 0 0 16px;
                    width: 480px; max-width: 92vw;
                    height: 100%;
                    box-shadow: -8px 0 40px rgba(0,0,0,0.12);
                    animation: cpDrawerIn 0.28s cubic-bezier(0.32,0.72,0,1);
                    display: flex; flex-direction: column;
                    overflow: hidden;
                }
                @keyframes cpDrawerIn {
                    from { transform: translateX(100%); }
                    to   { transform: translateX(0); }
                }
                .cp-modal-header {
                    padding: 20px 24px;
                    border-bottom: 1px solid #f3f4f6;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-shrink: 0;
                }
                .cp-modal-title { font-size: 18px; font-weight: 600; color: #0f172a; margin: 0; }
                .cp-modal-close {
                    width: 32px; height: 32px; border-radius: 8px; border: none;
                    background: none; color: #9ca3af; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 16px; transition: all 0.15s;
                }
                .cp-modal-close:hover { background: #f3f4f6; color: #374151; }
                .cp-modal-body { padding: 24px; display: flex; flex-direction: column; gap: 20px; flex: 1; overflow-y: auto; }
                .cp-field label {
                    display: block;
                    font-size: 12px; font-weight: 600;
                    text-transform: uppercase; letter-spacing: 0.07em;
                    color: #64748b; margin-bottom: 6px;
                }
                .cp-field input[type="text"] {
                    width: 100%; padding: 10px 14px;
                    border: 1.5px solid #e2e8f0; border-radius: 9px;
                    font-size: 14px; font-family: inherit; color: #0f172a;
                    outline: none;
                    transition: border-color 0.15s, box-shadow 0.15s;
                    box-sizing: border-box;
                }
                .cp-field input:focus {
                    border-color: #2563eb;
                    box-shadow: 0 0 0 3px rgba(37,99,235,0.12);
                }
                .cp-toggle-row {
                    display: flex; align-items: center; justify-content: space-between;
                    padding: 12px 14px;
                    background: #f8fafc; border-radius: 10px; border: 1.5px solid #e2e8f0;
                }
                .cp-toggle-label { font-size: 14px; font-weight: 500; color: #334155; }
                .cp-switch { position: relative; width: 42px; height: 24px; flex-shrink: 0; }
                .cp-switch input { opacity: 0; width: 0; height: 0; }
                .cp-switch-track {
                    position: absolute; inset: 0;
                    border-radius: 999px; background: #cbd5e1;
                    cursor: pointer; transition: background 0.2s;
                }
                .cp-switch input:checked + .cp-switch-track { background: #22c55e; }
                .cp-switch-track::after {
                    content: ''; position: absolute;
                    top: 3px; left: 3px; width: 18px; height: 18px;
                    border-radius: 50%; background: #fff;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.18);
                    transition: transform 0.2s;
                }
                .cp-switch input:checked + .cp-switch-track::after { transform: translateX(18px); }
                .cp-modal-footer {
                    padding: 16px 24px; border-top: 1px solid #f3f4f6;
                    display: flex; justify-content: flex-end; gap: 10px;
                    background: rgba(255,255,255,0.9); backdrop-filter: blur(8px); flex-shrink: 0;
                }
                .cp-btn-cancel {
                    padding: 9px 18px; border-radius: 8px; border: none;
                    background: none; color: #4b5563;
                    font-size: 13px; font-weight: 500; font-family: inherit;
                    cursor: pointer; transition: background 0.15s;
                }
                .cp-btn-cancel:hover { background: #f9fafb; }
                .cp-btn-save {
                    padding: 10px 20px; border-radius: 8px; border: none;
                    background: #cd6906; color: #fff;
                    font-size: 13px; font-weight: 600; font-family: inherit;
                    cursor: pointer;
                    display: inline-flex; align-items: center; gap: 6px;
                    box-shadow: 0 4px 12px rgba(205,105,6,0.3);
                    transition: background 0.15s, transform 0.1s;
                }
                .cp-btn-save:hover:not(:disabled) { background: #b85d05; transform: translateY(-1px); }
                .cp-btn-save:disabled { opacity: 0.65; cursor: not-allowed; }
                .cp-spinner {
                    width: 14px; height: 14px;
                    border: 2px solid rgba(255,255,255,0.4);
                    border-top-color: #fff; border-radius: 50%;
                    animation: cpSpin 0.7s linear infinite;
                }
                @keyframes cpSpin { to { transform: rotate(360deg); } }
            `}</style>

            <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", padding: '24px', background: '#fff' }}>
                {/* Header */}
                <div style={{ paddingBottom: 20, marginBottom: 24, borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                        <h1 style={{ fontSize: 20, fontWeight: 600, color: '#111827', margin: 0, lineHeight: 1.3 }}>{t('ayarlar.cephe.baslik')}</h1>
                        <p style={{ fontSize: 13, color: '#6b7280', margin: '3px 0 0' }}>{t('ayarlar.cephe.aciklama')}</p>
                    </div>
                    <button
                        onClick={() => setShowModal(true)}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', boxShadow: '0 1px 3px rgba(205,105,6,0.3)', transition: 'all 0.15s' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = '#a85505'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = '#cd6906'; (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                    >
                        <svg style={{ width: 14, height: 14, stroke: '#fff', strokeWidth: 2.5, fill: 'none' }} viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        {t('ayarlar.cephe.yeniKayit')}
                    </button>
                </div>

                {/* Card List */}
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                    {cepheler.length === 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 20px', color: '#9ca3af' }}>
                            <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.5 }}>🧭</div>
                            <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>{t('msg.henuzKayitYok')}</p>
                        </div>
                    ) : (
                        cepheler.map((cephe, idx) => {
                            const dirStyle = getDirectionStyle(cephe.cepheAdi);
                            const letter = getCompassLetter(cephe.cepheAdi);
                            return (
                                <div
                                    key={cephe.cepheID}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: idx < cepheler.length - 1 ? '1px solid #f3f4f6' : 'none', transition: 'background 0.13s' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <div style={{ width: 36, height: 36, borderRadius: 9, background: dirStyle.bg, color: dirStyle.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                                            {letter}
                                        </div>
                                        <div>
                                            <p style={{ fontSize: 14, fontWeight: 600, color: '#111827', margin: 0 }}>{cephe.cepheAdi}</p>
                                            <p style={{ fontSize: 12, margin: '2px 0 0', color: cephe.aktif ? '#059669' : '#e11d48', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <span style={{ width: 6, height: 6, borderRadius: '50%', background: cephe.aktif ? '#10b981' : '#f43f5e', display: 'inline-block' }} />
                                                {cephe.aktif ? t('durum.aktif') : t('durum.pasif')}
                                            </p>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 4 }}>
                                        <button title={t('btn.duzenle')} onClick={() => handleEdit(cephe)}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#2563eb', display: 'flex', transition: 'background 0.13s' }}
                                            onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')}
                                            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                        >
                                            <svg style={{ width: 15, height: 15, stroke: 'currentColor', strokeWidth: 2, fill: 'none' }} viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        </button>
                                        <button title={t('btn.sil')} onClick={() => toast.info(t('msg.silmeOnayGerekli'))}
                                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 6, color: '#f43f5e', display: 'flex', transition: 'background 0.13s' }}
                                            onMouseEnter={e => (e.currentTarget.style.background = '#fff1f2')}
                                            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                        >
                                            <svg style={{ width: 15, height: 15, stroke: 'currentColor', strokeWidth: 2, fill: 'none' }} viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="cp-overlay" onClick={handleCloseModal}>
                    <div className="cp-modal" onClick={e => e.stopPropagation()}>
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: 0 }}>{selectedCephe ? t('ayarlar.cephe.duzenle') : t('ayarlar.cephe.yeniEkle')}</h2>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', borderRadius: 8, color: '#9ca3af', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32 }} onClick={handleCloseModal}>✕</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="cp-modal-body">
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{t('ayarlar.cephe.cepheAdi')}</label>
                                    <input
                                        type="text"
                                        id="cepheAdiInput"
                                        placeholder={t('ayarlar.cephe.placeholder')}
                                        value={formData.cepheAdi}
                                        onChange={e => setFormData({ ...formData, cepheAdi: e.target.value })}
                                        required
                                        maxLength={50}
                                        autoFocus
                                        style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: '#111827', outline: 'none', boxSizing: 'border-box' as const, transition: 'border-color 0.15s, box-shadow 0.15s' }}
                                        onFocus={e => { e.target.style.borderColor = '#111827'; e.target.style.boxShadow = '0 0 0 3px rgba(17,24,39,0.08)'; }}
                                        onBlur={e => { e.target.style.borderColor = '#d1d5db'; e.target.style.boxShadow = 'none'; }}
                                    />
                                </div>
                                <div className="cp-toggle-row">
                                    <span className="cp-toggle-label">{t('label.durumAktif')}</span>
                                    <label className="cp-switch">
                                        <input
                                            type="checkbox"
                                            checked={formData.aktif}
                                            onChange={e => setFormData({ ...formData, aktif: e.target.checked })}
                                        />
                                        <span className="cp-switch-track" />
                                    </label>
                                </div>
                            </div>
                            <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 10, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', flexShrink: 0 }}>
                                <button
                                    type="button"
                                    style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' }}
                                    onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                                    onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
                                    onClick={handleCloseModal}
                                >{t('btn.iptal')}</button>
                                <button
                                    type="submit"
                                    disabled={saving}
                                    style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: '#cd6906', color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: saving ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, boxShadow: '0 1px 3px rgba(205,105,6,0.3)', opacity: saving ? 0.75 : 1 }}
                                    onMouseEnter={e => { if (!saving) { e.currentTarget.style.background = '#a85505'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(205,105,6,0.4)'; }}}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#cd6906'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(205,105,6,0.3)'; }}
                                >
                                    {saving && <span className="cp-spinner" />}
                                    {selectedCephe ? t('btn.guncelle') : t('btn.ekle')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default Cepheler;
